import type {
  Carousel,
  CarouselFromDraftRequest,
  PostDraftSet,
  PostsFromLinkRequest,
  PostsFromOfferRequest,
  PostsFromTopicRequest,
  PostsSaveRequest,
} from '@smartapply/shared';

import { SmartApplyApiError } from './api-client.js';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3030';

let csrfTokenCache: string | null = null;

async function ensureCsrfToken(): Promise<string> {
  if (csrfTokenCache) return csrfTokenCache;
  const res = await fetch(`${API_URL}/api/v1/csrf-token`, { credentials: 'include' });
  const data = (await res.json()) as { csrfToken: string };
  csrfTokenCache = data.csrfToken;
  return data.csrfToken;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const token = await ensureCsrfToken();
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  if (res.status === 204) return undefined as T;
  if (res.ok) return (await res.json()) as T;
  let payload: { error?: { code: string; message: string; details?: unknown } } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    /* ignore */
  }
  throw new SmartApplyApiError(
    payload.error?.code ?? `http_${res.status}`,
    payload.error?.message ?? `HTTP ${res.status}`,
    res.status,
    payload.error?.details,
  );
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (res.ok) return (await res.json()) as T;
  let payload: { error?: { code: string; message: string } } = {};
  try {
    payload = (await res.json()) as typeof payload;
  } catch {
    /* ignore */
  }
  throw new SmartApplyApiError(
    payload.error?.code ?? `http_${res.status}`,
    payload.error?.message ?? `HTTP ${res.status}`,
    res.status,
  );
}

/**
 * Anexa profile cacheado em sessionStorage como fallback. Server usa só
 * quando o snapshot in-memory expirou — evita 412 "faça upload de novo"
 * depois de restart do container ou inatividade longa.
 */
async function withCachedProfile<T extends object>(req: T): Promise<T & { profile?: unknown }> {
  if ('profile' in req && (req as { profile?: unknown }).profile) {
    return req;
  }
  const { getCachedProfile } = await import('./profile-session.js');
  const profile = getCachedProfile();
  if (!profile) return req;
  return { ...req, profile };
}

export async function generatePostsFromLink(req: PostsFromLinkRequest): Promise<PostDraftSet> {
  const body = await withCachedProfile(req);
  return postJson<PostDraftSet>('/api/v1/posts/from-link', body);
}

export async function generatePostsFromTopic(req: PostsFromTopicRequest): Promise<PostDraftSet> {
  const body = await withCachedProfile(req);
  return postJson<PostDraftSet>('/api/v1/posts/from-topic', body);
}

export async function generatePostsFromOffer(req: PostsFromOfferRequest): Promise<PostDraftSet> {
  const body = await withCachedProfile(req);
  return postJson<PostDraftSet>('/api/v1/posts/from-offer', body);
}

export async function generateCarouselFromDraft(req: CarouselFromDraftRequest): Promise<Carousel> {
  const body = await withCachedProfile(req);
  return postJson<Carousel>('/api/v1/posts/carousel-from-draft', body);
}

export async function savePosts(
  req: PostsSaveRequest,
): Promise<{ id: string; createdAt: string }> {
  return postJson('/api/v1/posts/save', req);
}

export interface SavedPostsListItem {
  id: string;
  createdAt: string;
  sourceType: 'link' | 'topic';
  sourceInput: string;
  label: string | null;
  archivedAt: string | null;
  selectedDraftId: string | null;
  draftCount: number;
  modelUsed: string;
}

export async function listSavedPosts(includeArchived = false): Promise<{ items: SavedPostsListItem[] }> {
  const q = includeArchived ? '?archived=true' : '';
  return getJson(`/api/v1/posts/drafts${q}`);
}

export interface PublishedDraftEntry {
  draftId: string | null;
  publishedAt: string;
}

export interface SavedPostDetail {
  id: string;
  createdAt: string;
  sourceType: 'link' | 'topic' | 'offer';
  sourceInput: string;
  label: string | null;
  archivedAt: string | null;
  selectedDraftId: string | null;
  // Ponteiros pra "última publicação" (compat) — derivados do publishedDrafts.
  publishedAt: string | null;
  publishedDraftId: string | null;
  // Histórico completo de publicações desse set (1 por dia, etc).
  publishedDrafts: PublishedDraftEntry[];
  draftSet: PostDraftSet;
}

export async function getSavedPost(id: string): Promise<SavedPostDetail> {
  return getJson(`/api/v1/posts/drafts/${id}`);
}

export async function archiveSavedPost(id: string): Promise<void> {
  await postJson(`/api/v1/posts/drafts/${id}/archive`, {});
}

export async function deleteSavedPost(id: string): Promise<void> {
  await postJson(`/api/v1/posts/drafts/${id}`, {}); // dummy — tem que ser DELETE
}
